import { nanoid } from 'nanoid'
import { ApiError } from '@/util/errors/api-error'
import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { getTableAnalysis } from '../../fetch/get-table-analysis'
import { AnalysisActions } from './slice'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { postAddTableDocuments } from '../../fetch/post-add-table-documents'
import { postTableDocumentUploadFailure } from '../../fetch/post-table-document-upload-failure'
import { tableUploadRegistered } from './upload-actions'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import { TableDocumentSource } from '@/analytics/enums/table.enums'
import { consumeCreationDocumentCount, trackTableDocumentsAdded } from '@/analytics/utils/table-analytics'

export const tableAnalysisListenerMiddleware = createListenerMiddleware()
const listen = tableAnalysisListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

listen({
  actionCreator: tableUploadRegistered,
  effect: async ({ payload: { matterId, tableId, clientTmpId } }, api) => {
    // Unlike a popup callback, this also sees initialization by the provider after navigation.
    const existing = api.getState().uppyFileUploadState.files[clientTmpId]
    let documentId = existing?.client.serverFileId
    if (!documentId) {
      if (existing && ['error', 'unsupported_type'].includes(existing.client.uploadStatus)) return
      const [initialized] = await api.take(
      (action) =>
        (UppyFileUploadActions.fileInitialized.match(action) ||
          UppyFileUploadActions.uploadFailed.match(action) ||
          UppyFileUploadActions.unsupportedTypeFileAdded.match(action) ||
          UppyFileUploadActions.validationFailedFileAdded.match(action) ||
          UppyFileUploadActions.fileRemoved.match(action)) &&
        action.payload.clientTmpId === clientTmpId
    )
    if (!UppyFileUploadActions.fileInitialized.match(initialized)) return
      documentId = initialized.payload.serverFileId
    }
    // Observe completion before awaiting attachment: a small upload can finish during that request.
    const status = api.getState().uppyFileUploadState.files[clientTmpId]?.client.uploadStatus
    const finished = status === 'uploaded' ? Promise.resolve(true) : status === 'error' ? Promise.resolve(false) : api.take(
      (action) =>
        (UppyFileUploadActions.uploadCompleted.match(action) ||
          UppyFileUploadActions.uploadFailed.match(action) ||
          UppyFileUploadActions.fileRemoved.match(action)) &&
        action.payload.clientTmpId === clientTmpId
    ).then(([action]) => UppyFileUploadActions.uploadCompleted.match(action))
    let failureNotified = false
    const reportFailure = async (error: unknown) => {
      const message = error instanceof ApiError ? error.message : 'Unable to update the table upload.'
      // One notification per file attempt; subsequent failures still reconcile and update state.
      if (!failureNotified) {
        failureNotified = true
        api.dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationMediumMs }))
      }
      // Reconcile a request that may have committed before its response was lost.
      try {
        const state = api.getState().tableAnalysis
        const requestId = nanoid()
        api.dispatch(AnalysisActions.readStarted({ tableId, requestId }))
        const run = await getTableAnalysis(tableId, api.signal, undefined, state.tableId === tableId ? state.rowIds : undefined)
        api.dispatch(AnalysisActions.received({ tableId, run, requestId }))
      } catch {
        // Ordinary table polling will try again.
      }
      api.dispatch(AnalysisActions.failed({ tableId, error: message }))
    }
    let attached = false
    try {
      const run = await postAddTableDocuments(matterId, tableId, [documentId])
      attached = true
      api.dispatch(AnalysisActions.uploadAttached({ tableId, clientTmpId }))
      api.dispatch(AnalysisActions.received({ tableId, run }))
    } catch (error) {
      const filename = api.getState().uppyFileUploadState.files[clientTmpId]?.client.filename ?? 'This file'
      const detail = error instanceof Error ? error.message : 'Unable to add the file.'
      await reportFailure(new ApiError(error instanceof ApiError ? error.status : 400,
        `${filename} could not be added to the table. ${detail} If its upload completes, the file remains available in the matter.`))
    }
    // Keep listening even when the attachment response failed: it may have committed.
    const uploadFinished = await finished
    if (!uploadFinished) {
      try {
        const run = await postTableDocumentUploadFailure(tableId, documentId)
        api.dispatch(AnalysisActions.received({ tableId, run }))
      } catch (error) {
        await reportFailure(error)
      }
    }
    const alreadyCounted = consumeCreationDocumentCount(tableId, clientTmpId, documentId)
    if (uploadFinished && attached && !alreadyCounted) {
      const track = getTrackEventGlobal()
      if (track) trackTableDocumentsAdded(track, tableId, 1, TableDocumentSource.Upload)
    }
  },
})
listen({
  actionCreator: AnalysisActions.watch,
  effect: async ({ payload: tableId }, api) => {
    api.cancelActiveListeners()
    const poll = api.fork(async ({ signal, delay }) => {
      while (!signal.aborted) {
        const requestId = nanoid()
        api.dispatch(AnalysisActions.readStarted({ tableId, requestId }))
        try {
          const rowIds = api.getState().tableAnalysis.rowIds
          const run = await getTableAnalysis(tableId, signal, undefined, rowIds)
          if (signal.aborted) return
          if (rowIds.join() === api.getState().tableAnalysis.rowIds.join()) {
            api.dispatch(AnalysisActions.received({ tableId, run, requestId }))
          }
        } catch (error) {
          if (signal.aborted) return
          api.dispatch(AnalysisActions.failed({ tableId, requestId, error: error instanceof ApiError ? error.message : 'Unable to load progress.' }))
        }
        const run = api.getState().tableAnalysis.run
        const active = run ? run.counts.pending + run.counts.running > 0 : false
        await delay(active ? 3000 : 30000)
      }
    })
    await api.condition((action) => AnalysisActions.stop.match(action) && action.payload === tableId)
    poll.cancel()
  },
})
listen({
  actionCreator: AnalysisActions.control,
  effect: async ({ payload }, api) => {
    try {
      const run = await getTableAnalysis(payload.tableId, api.signal, payload.action)
      api.dispatch(AnalysisActions.received({ tableId: payload.tableId, run }))
      if (payload.action !== 'cancel') api.dispatch(AnalysisActions.refresh(payload.tableId))
    } catch (error) {
      api.dispatch(AnalysisActions.failed({ tableId: payload.tableId, error: error instanceof ApiError ? error.message : 'Unable to update analysis.' }))
    } finally {
      api.dispatch(AnalysisActions.controlFinished(payload.tableId))
    }
  },
})

listen({
  actionCreator: AnalysisActions.visibleRows,
  effect: async ({ payload }, api) => {
    api.cancelActiveListeners()
    const requestId = nanoid()
    api.dispatch(AnalysisActions.readStarted({ tableId: payload.tableId, requestId }))
    try {
      const run = await getTableAnalysis(payload.tableId, api.signal, undefined, payload.rowIds)
      if (!api.signal.aborted) api.dispatch(AnalysisActions.received({ tableId: payload.tableId, run, requestId }))
    } catch (error) {
      if (!api.signal.aborted)
        api.dispatch(AnalysisActions.failed({ tableId: payload.tableId, requestId, error: error instanceof ApiError ? error.message : 'Unable to load progress.' }))
    }
  },
})

// Mutation completions can refresh the open table, but cannot reopen a closed watcher.
listen({
  actionCreator: AnalysisActions.refresh,
  effect: ({ payload: tableId }, api) => {
    if (api.getState().tableAnalysis.tableId === tableId) api.dispatch(AnalysisActions.watch(tableId))
  },
})
